import {
  Affix,
  AppShell,
  Button,
  Header,
  Loader,
  Navbar,
  Transition,
} from '@mantine/core';
import { useHotkeys, useScrollLock, useWindowScroll } from '@mantine/hooks';
import { openSpotlight, SpotlightProvider } from '@mantine/spotlight';
import type { SpotlightAction } from '@mantine/spotlight';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import {
  NavigateFunction,
  Outlet,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ArrowUp, Folder, Search } from 'tabler-icons-react';

import { SearchedIssue, useGetAllIssues } from 'api/issues/getAllIssues';
import { useGetAllOrgs } from 'api/organizations/getAllOrgs';
import { useGetOrg } from 'api/organizations/getOrg';
import { useGetProjects } from 'api/projects/getProjects';
import { BugIcon, EpicIcon, StoryIcon, SubtaskIcon, TaskIcon } from 'assets/icons';
import BottomNav from 'components/BottomNav/BottomNav';
import { useBoardEvents } from 'hooks/useBoardEvents';
import { Issue, User } from 'types';
import { getRecentIssues } from 'utils/recentIssues';

import HeaderContent from './Header';
import styles from './Layout.module.css';
import NavbarContent from './Navbar';

type LayoutProps = {
  user: User;
};

const typeIcons: Record<Issue['type'], React.ReactNode> = {
  task: <TaskIcon />,
  story: <StoryIcon />,
  bug: <BugIcon />,
  epic: <EpicIcon />,
  'sub-task': <SubtaskIcon />,
};

const getIssueActions = (
  issues: SearchedIssue[],
  navigate: NavigateFunction
): SpotlightAction[] =>
  issues.map((issue) => ({
    id: `issue-${issue._id}`,
    title: issue.title,
    description: `Issue · ${issue.type}`,
    icon: typeIcons[issue.type],
    onTrigger: () => {
      navigate(`/projects/${issue.project}?selectedIssue=${issue._id}`);
    },
  }));

type ProjectForSearch = { _id: string; name: string };
const getProjectActions = (
  projects: ProjectForSearch[],
  navigate: NavigateFunction
): SpotlightAction[] =>
  projects.map((project) => ({
    id: `project-${project._id}`,
    title: project.name,
    description: 'Project',
    icon: <Folder size={20} />,
    onTrigger: () => navigate(`/projects/${project._id}`),
  }));

function Layout({ user }: LayoutProps) {
  const [opened, setOpened] = useState(false);
  const queryClient = useQueryClient();
  const superAdminNeedsOrg = user.role === 'super admin' && !user.org;
  const { data: orgs } = useGetAllOrgs(superAdminNeedsOrg);
  const activeOrgId = orgs?.[0]?._id;
  const { data: activeOrg } = useGetOrg(
    superAdminNeedsOrg ? activeOrgId : undefined
  );

  useEffect(() => {
    if (!superAdminNeedsOrg || !activeOrg) {
      return;
    }
    queryClient.setQueryData(['auth-user'], {
      ...user,
      org: activeOrg,
    });
  }, [activeOrg, queryClient, superAdminNeedsOrg, user]);

  const { data: issuesData, isSuccess: issuesSuccess, isFetching: issuesFetching } =
    useGetAllIssues(user.org?._id);
  const { data: projectsData, isSuccess: projectsSuccess } = useGetProjects(
    user.org?._id
  );
  const navigate = useNavigate();
  const [recentIssues, setRecentIssues] = useState(getRecentIssues);
  useBoardEvents();

  useEffect(() => {
    const onRecentChange = () => setRecentIssues(getRecentIssues());
    window.addEventListener('flowboard-recent-issues-changed', onRecentChange);
    return () => window.removeEventListener('flowboard-recent-issues-changed', onRecentChange);
  }, []);

  const searchReady = issuesSuccess && projectsSuccess;
  const recentActions: SpotlightAction[] = recentIssues.map((r) => ({
    id: `recent-${r.id}`,
    title: r.title,
    description: 'Recently viewed',
    icon: <Search size={20} />,
    onTrigger: () => {
      navigate(`/projects/${r.projectId}?selectedIssue=${r.id}`);
    },
  }));
  const searchActions: SpotlightAction[] = [
    ...recentActions,
    ...getProjectActions(projectsData?.projects ?? [], navigate),
    ...getIssueActions(issuesData ?? [], navigate),
  ];

  const [scroll, scrollTo] = useWindowScroll();
  const setScrollLocked = useScrollLock()[1];

  const [searchParams] = useSearchParams();
  const selectedIssue = searchParams.get('selectedIssue');

  useHotkeys([
    ['mod + K', () => { if (selectedIssue) return; openSpotlight(); }],
    [
      '/',
      (e) => {
        const target = document.activeElement?.tagName?.toLowerCase();
        if (target === 'input' || target === 'textarea' || (document.activeElement as HTMLElement)?.getAttribute?.('contenteditable') === 'true') return;
        e.preventDefault();
        if (selectedIssue) return;
        openSpotlight();
      },
    ],
  ]);

  return (
    <SpotlightProvider
      actions={searchReady ? searchActions : []}
      searchIcon={issuesFetching ? <Loader size={20} /> : <Search size={20} />}
      searchPlaceholder="Search issues and projects"
      nothingFoundMessage="No issues or projects found"
      overlayOpacity={0.5}
      overlayBlur={0}
      highlightQuery
      highlightColor="violet"
      styles={{
        actionIcon: {
          flexShrink: 0,
        },
      }}
      shortcut={null}
    >
      <AppShell
        padding="md"
        header={
          <Header
            height={{ base: 60, sm: 70 }}
            px="md"
            sx={{
              '@media (max-width: 319px)': {
                paddingLeft: 10,
                paddingRight: 10,
              },
            }}
          >
            <HeaderContent
              opened={opened}
              onToggleNavbar={() => {
                setOpened((o) => !o);
                setScrollLocked((l) => !l);
              }}
              onClose={() => {
                setOpened(false);
                setScrollLocked(false);
              }}
              user={user}
            />
          </Header>
        }
        navbar={
          <Navbar
            p="md"
            hiddenBreakpoint="sm"
            hidden={!opened}
            width={{ sm: 250, lg: 300 }}
            aria-label="Main"
            className={styles.nav}
          >
            <NavbarContent
              onClose={() => {
                setOpened(false);
                setScrollLocked(false);
              }}
              user={user}
            />
          </Navbar>
        }
      >
        <a
          href="#main-content"
          style={{
            position: 'absolute',
            left: -9999,
            zIndex: 9999,
            padding: '8px 16px',
            background: 'var(--mantine-color-blue-6)',
            color: '#fff',
            borderRadius: 4,
          }}
          onFocus={(e) => {
            e.currentTarget.style.left = '16px';
            e.currentTarget.style.top = '80px';
          }}
          onBlur={(e) => {
            e.currentTarget.style.left = '-9999px';
          }}
        >
          Skip to main content
        </a>
        <main id="main-content" tabIndex={-1} className={styles.mainWithBottomNav} style={{ outline: 'none' }}>
          <Outlet context={{ user }} />
        </main>
        <BottomNav />
        <Affix position={{ bottom: 70, right: 20 }}>
          <Transition transition="slide-up" mounted={scroll.y > 800 && !opened}>
            {(transitionStyles) => (
              <Button
                leftIcon={<ArrowUp size={16} />}
                style={transitionStyles}
                onClick={() => scrollTo({ y: 0 })}
              >
                Scroll to Top
              </Button>
            )}
          </Transition>
        </Affix>
      </AppShell>
    </SpotlightProvider>
  );
}

export default Layout;
